import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useContext } from 'react';
import SingleCustomerContext from '../Customer-Context/SingleCustomerContext';

interface UseCustomerTableDataReturn {
  mainItems: any[];
  expandedItemsIds: string[];
  groupBy: string;
  setExpandedItemsIds: Dispatch<SetStateAction<string[]>>;
  setGroupBy: Dispatch<SetStateAction<string>>;
  sortedIdeas: any[];
}

export const useCustomerTableData = (): UseCustomerTableDataReturn => {
  const { 
    ideas = [], 
    domains = [], 
    allComponents = [],
    threeSixtyConfig,
    ideaStatuses = [],
    threeSixtyColumns = [],
    groupBy,
    setGroupBy
  } = useContext(SingleCustomerContext);

  const dropdownMenu = threeSixtyConfig?.dropdownMenu || [];
  const demand = dropdownMenu.find((item: any) => item.groupBy === 'demand');
  const scales: any[] = demand?.scales || [];

  const [expandedItemsIds, setExpandedItemsIds] = useState<string[]>([]);
  const [mainItems, setMainItems] = useState<any[]>([]);
  
  // Function to sort ideas based on threeSixtyColumns order
  const sortIdeasByStatusOrder = (ideasToSort: any[]) => {
    if (!threeSixtyColumns || threeSixtyColumns.length === 0) {
      return ideasToSort;
    }

    // Create a map of status to column index for sorting
    const statusToColumnIndex: { [key: string]: number } = {};
    
    threeSixtyColumns.forEach((column, columnIndex) => {
      if (column.statuses && Array.isArray(column.statuses)) {
        column.statuses.forEach((status: string) => {
          statusToColumnIndex[status.toLowerCase()] = columnIndex;
        });
      }
    });

    return [...ideasToSort].sort((a, b) => {
      const aStatus = a?.status?.toLowerCase();
      const bStatus = b?.status?.toLowerCase();
      
      const aColumnIndex = statusToColumnIndex[aStatus] ?? Number.MAX_SAFE_INTEGER;
      const bColumnIndex = statusToColumnIndex[bStatus] ?? Number.MAX_SAFE_INTEGER;
      
      // Primary sort by status order
      if (aColumnIndex !== bColumnIndex) {
        return aColumnIndex - bColumnIndex;
      }
      
      // Secondary sort by alphabetical order (ABC)
      const aName = (a?.name || '').toLowerCase();
      const bName = (b?.name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  };

  // Sync with threeSixtyConfig changes
  useEffect(() => {
    if (threeSixtyConfig?.groupBy && threeSixtyConfig.groupBy !== groupBy) {
      setGroupBy(threeSixtyConfig.groupBy);
    }
  }, [threeSixtyConfig]);

  useEffect(() => {

    

    // Ensure we have valid data before proceeding
    if (!ideas || !domains || !allComponents) {
      console.log('Missing required data:', { ideas, domains, allComponents });
      setMainItems([]);
      return;
    }

    const sortedIdeas = sortIdeasByStatusOrder(ideas)

    let mainItemsArr;
    if (groupBy === 'domainId') {
      mainItemsArr = domains;
    } else if (groupBy === 'componentId') {
      mainItemsArr = allComponents;
    } else if (groupBy === 'status') {
      // Get unique statuses from ideas and order them based on threeSixtyColumns
      const statusOrder = threeSixtyColumns.reduce((acc: { [key: string]: number }, column, index) => {
        column.statuses.forEach((status: string) => {
          if (typeof status === 'string') {
            acc[status.toLowerCase()] = index;
          }
        });
        return acc;
      }, {});

      const uniqueStatuses = Array.from(new Set(sortedIdeas.map(idea => idea?.status)));
      mainItemsArr = uniqueStatuses
        .map(status => ({
          _id: status,
          name: status,
          status: status,
          order: typeof status === 'string' ? (statusOrder[status.toLowerCase()] ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER
        }))
        .sort((a, b) => a.order - b.order);
    } else if (groupBy === 'demand') {
      // Group ideas by demand using scales as boundaries
     
      
      // Create a map of demand values to their order based on scales
      const demandOrder = scales.reduce((acc: { [key: string]: number }, scale, index) => {
        acc[scale.label.toLowerCase()] = index;
        return acc;
      }, {});

      // Get unique demand values from ideas
      const uniqueDemands = Array.from(new Set(sortedIdeas.map(idea => idea?.demand)));

      // Create demand groups with proper ordering and idea counts
      mainItemsArr = uniqueDemands
        .map(demand => {
          const ideasInGroup = sortedIdeas.filter(idea => idea?.demand === demand);
          return {
            _id: demand,
            name: demand,
            demand: demand,
            order: demandOrder[demand?.toLowerCase()] ?? Number.MAX_SAFE_INTEGER,
            ideasCount: ideasInGroup.length
          };
        })
        .sort((a, b) => a.order - b.order);
    } else {
      mainItemsArr = domains; // Default to domains
    }
    
    const relevantItems = mainItemsArr
      .filter(relevantItem => {
        if (groupBy === 'status') {
          return sortedIdeas?.some(idea => idea.status === relevantItem.status);
        }
        if (groupBy === 'demand') {
          return relevantItem.ideasCount > 0;
        }
        return sortedIdeas?.some(idea => idea[groupBy] === relevantItem._id);
      })
      .map(relevantItem => ({
        ...relevantItem,
        id: relevantItem?._id,
        ideasCount: groupBy === 'demand' ? relevantItem.ideasCount :sortedIdeas?.filter(idea => {
          if (groupBy === 'status') {
            return idea.status === relevantItem.status;
          }
          return idea[groupBy] === relevantItem._id;
        }).length
      }));

    setMainItems(relevantItems);
    
  }, [domains, threeSixtyConfig, ideas, allComponents, ideaStatuses, groupBy, scales, threeSixtyColumns]);

  return {
    mainItems,
    expandedItemsIds,
    groupBy,
    setExpandedItemsIds,
    setGroupBy,
    sortedIdeas: sortIdeasByStatusOrder(ideas)
  };
}; 